import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  Grid,
  Tab,
  Tabs,
} from "@mui/material";
import ConfigurableTable, { TableSortType } from "components/ConfigurableTable";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import DeleteIconButton from "components/IconButtons/DeleteIconButton";
import EditIconButton from "components/IconButtons/EditIconButton";
import SnackbarMessage from "components/SnackbarMessage";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { MRT_ColumnDef } from "material-react-table";
import { useSkuQualificationsQuery } from "pages/SkusPage/SkuQualifications/useSkuQualificationsQuery";
import { useCallback, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AccreditationBodiesGroupedViewEnabledRequest,
  SkuQualification,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import persistanceStore from "stores/persistanceStore";
import ExpandedChildTable from "./ExpandedChildTable";
import SkuQualificationFilter from "./SkuQualificationFilter";
import SkuQualificationForm from "./SkuQualificationForm";
import {
  countLicensureBodies,
  groupSkuQualifications,
  resolveSkuQualificationDeleteIds,
  SkuQualificationGroupBy,
  SkuQualificationGroupModel,
} from "helpers/skuQualificationHelpers";
import { MultiValueCell } from "./MultiValueCell";
import { RollupCell } from "./RollupCell";
import SkuQualificationStatusChip from "./SkuQualificationStatusChip";
import LegacyQualificationsTable from "./legacy/LegacyQualificationsTable";
import { LegacyQualificationGroup } from "./legacy/grouping";
import AcpeValidationDialog from "./AcpeValidationDialog";
import BulkSkuQualificationConfirmDialog from "./BulkSkuQualificationConfirmDialog";
import { InternalValidationDialog } from "./InternalValidationDialog";
import QualificationWizardDialog from "./wizard/QualificationWizardDialog";
import EditQualificationDialog from "./wizard/EditQualificationDialog";
import { useAccreditationChainRows } from "./wizard/useAccreditationChainRows";
import { useQualificationSave } from "./wizard/useQualificationSave";
import { toWizardEditState, type WizardEditState } from "./wizard/wizardSave";

const GROUP_TABS: { value: SkuQualificationGroupBy; labelId: string }[] = [
  { value: "licensureBody", labelId: "groupByLicensureBodyLabel" },
  { value: "accreditationBody", labelId: "groupByAccreditationBodyLabel" },
  { value: "submissionAuthority", labelId: "groupBySubmissionAuthorityLabel" },
];

// Profession is part of a qualification's identity and is never reassigned on update.
const EDIT_DISABLED_FIELDS = new Set(["professionIds"]);
// A group edit writes every member, so its credit lines stay read-only.
const GROUP_EDIT_DISABLED_FIELDS = new Set(["professionIds", "creditLines"]);

// Columns hidden by default per the ticket's "Columns per view" table; still
// toggleable in the picker. Seeds initial visibility only (see defaultColumnVisibility).
const DEFAULT_HIDDEN_COLUMNS = {
  "verificationStatus.name": false,
  approvalCode: false,
  approvalDate: false,
  credits: false,
  minimumCredit: false,
  partialCreditIncrement: false,
};

type SkuQualificationsProps = {
  isReadonly?: boolean;
} & SkuFormProps;

const SkuQualifications = ({
  sku,
  isReadonly,
  ...props
}: SkuQualificationsProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [
    skuQualificationToDelete,
    setSkuQualificationToDelete,
  ] = useState<SkuQualification | SkuQualificationGroupModel | null>(null);
  const [skuQualificationToEdit, setSkuQualificationToEdit] = useState<
    | SkuQualification
    | SkuQualificationGroupModel
    | LegacyQualificationGroup
    | null
  >(null);
  const [isChildEdit, setIsChildEdit] = useState(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createDialogOpen, createDialogOpenActions] = useBoolean(false);
  const [wizardOpen, wizardOpenActions] = useBoolean(false);
  const [editState, setEditState] = useState<WizardEditState | null>(null);
  // Initial server fetch order; the grouped view re-sorts client-side (enableClientDataOps),
  // the pre-grouped one drives these setters from its own column headers.
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [groupBy, setGroupBy] = useState<SkuQualificationGroupBy>(
    "accreditationBody",
  );
  const creditSubmissionExpirationDateFrom = persistanceStore.useState(
    (s) => s.creditSubmissionExpirationDateFrom,
  );

  const { data, isFetching } = useSkuQualificationsQuery(
    sku.id!,
    order,
    orderBy,
  );

  const { data: groupedViewSetting } = useQuery(
    ["accreditationBodiesGroupedViewSetting", sku.ownerOrganizationId],
    () =>
      skuService.getAccreditationBodiesGroupedViewEnabled(
        new AccreditationBodiesGroupedViewEnabledRequest({
          organizationId: sku.ownerOrganizationId!,
        }),
      ),
    { enabled: !!sku.ownerOrganizationId },
  );

  const isGroupedViewEnabled = groupedViewSetting?.data?.value === "true";

  const closeWizard = () => {
    wizardOpenActions.setFalse();
    setEditState(null);
  };

  const onWizardSaved = () => {
    const messageId = editState
      ? "skuAccreditationBodyUpdated"
      : "skuAccreditationBodyCreated";
    closeWizard();
    setSnackbarMessage(new SnackbarMessageProps(t({ id: messageId })));
    props.onSkuUpdated && props.onSkuUpdated();
  };

  const {
    submit: submitQualifications,
    internalValidation,
    bulkConfirm,
    acpeValidation,
  } = useQualificationSave(sku, onWizardSaved);

  // Only fetched while a wizard is open: it is one call per organization qualification set.
  const { rows: referenceRows, isLoading: isLoadingRows } =
    useAccreditationChainRows(sku.ownerOrganizationId!, wizardOpen);

  const skuQualifications = useMemo(
    () => groupSkuQualifications(data?.data ?? [], groupBy),
    [data?.data, groupBy],
  );

  // The state also holds plain SkuQualifications (the legacy view), so the group shape is
  // narrowed the same way resolveSkuQualificationDeleteIds narrows it.
  const deleteMemberCount =
    skuQualificationToDelete &&
    "isGroup" in skuQualificationToDelete &&
    skuQualificationToDelete.isGroup
      ? skuQualificationToDelete.memberCount
      : 0;

  const handleDelete = useCallback(() => {
    const target = skuQualificationToDelete;
    setSkuQualificationToDelete(null);
    if (!target) return;

    // AB-level delete: a group deletes all its member Licensure rows, a single row itself.
    const ids = resolveSkuQualificationDeleteIds(target);

    if (ids.length === 0) return;

    return registerPromise(
      Promise.all(ids.map((id) => skuService.deleteSkuQualification(id))),
    ).then(() => {
      queryClient.invalidateQueries(["skuQualifications", sku.id!]);

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "skuAccreditationBodyDeleted" })),
      );

      props.onSkuUpdated && props.onSkuUpdated();
    });
  }, [
    skuQualificationToDelete,
    props,
    queryClient,
    registerPromise,
    skuService,
    t,
    sku.id,
  ]);

  const columns: Array<MRT_ColumnDef<SkuQualificationGroupModel>> = [
    {
      id: "status",
      header: t({ id: "statusLabel" }),
      size: 90,
      enableSorting: false,
      enableHiding: false,
      enableColumnOrdering: false,
      visibleInShowHideMenu: false,
      accessorFn: (row) => row,
      Cell: ({ row }) =>
        row.original.isExpired ? (
          <Chip size="small" label={t({ id: "expiredChipLabel" })} />
        ) : null,
    },
    {
      id: "licensureBody",
      header: t({ id: "licensureBody" }),
      enableHiding: false,
      enableColumnOrdering: false,
      accessorFn: (row) => row.licensureBodyName,
      Cell: ({ row }) => (
        <RollupCell
          row={row.original}
          value={row.original.licensureBodyName}
          getMemberValue={(m) => m.licensureBodyName}
          countNoun={t({ id: "licensureBodies" })}
          count={
            groupBy === "accreditationBody" && row.original.isGroup
              ? countLicensureBodies(row.original.childQualifications)
              : undefined
          }
        />
      ),
    },
    {
      id: "accreditationBody",
      header: t({ id: "accreditationBodyLabel" }),
      enableHiding: false,
      enableColumnOrdering: false,
      accessorFn: (row) => row.accreditationBodyName,
      Cell: ({ row }) => (
        <RollupCell
          row={row.original}
          value={row.original.accreditationBodyName}
          getMemberValue={(m) => m.accreditationBodyName}
          countNoun={t({ id: "accreditationBodies" })}
        />
      ),
    },
    {
      id: "submissionAuthority",
      header: t({ id: "submissionAuthority" }),
      enableHiding: false,
      enableColumnOrdering: false,
      accessorFn: (row) => row.submissionAuthorityName,
      Cell: ({ row }) => (
        <RollupCell
          row={row.original}
          value={row.original.submissionAuthorityName}
          getMemberValue={(m) => m.submissionAuthorityName}
          countNoun={t({ id: "submissionAuthorities" })}
        />
      ),
    },
    {
      accessorKey: "profession.name",
      header: t({ id: "professionLabel" }),
      enableHiding: false,
      enableColumnOrdering: false,
      Cell: ({ row }) => (
        <MultiValueCell value={row.original.profession?.name} />
      ),
    },
    {
      id: "verificationStatus.name",
      header: t({ id: "skuValidationLabel" }),
      enableSorting: false,
      accessorFn: (row) => row,
      Cell: ({ row }) => <SkuQualificationStatusChip value={row.original} />,
    },
    {
      accessorKey: "courseNumber",
      header: t({ id: "courseNumber" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.courseNumber} />,
    },
    {
      accessorKey: "approvalCode",
      header: t({ id: "approvalCode" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.approvalCode} />,
    },
    {
      accessorKey: "approvalDate",
      header: t({ id: "approvalDate" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.approvalDate} />,
    },
    {
      accessorKey: "releaseDate",
      header: t({ id: "skuFormReleaseDateLabel" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.releaseDate} />,
    },
    {
      accessorKey: "creditSubmissionExpirationDate",
      header: t({ id: "skuFormCreditSubmissionExpirationDateLabel" }),
      Cell: ({ row }) => (
        <MultiValueCell value={row.original.creditSubmissionExpirationDate} />
      ),
    },
    {
      accessorKey: "credits",
      header: t({ id: "credits" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.credits} />,
    },
    {
      accessorKey: "minimumCredit",
      header: t({ id: "minimumCredit" }),
      Cell: ({ row }) => <MultiValueCell value={row.original.minimumCredit} />,
    },
    {
      accessorKey: "partialCreditIncrement",
      header: t({ id: "partialCreditIncrement" }),
      Cell: ({ row }) => (
        <MultiValueCell value={row.original.partialCreditIncrement} />
      ),
    },
  ];

  // Editing happens on the By-Accreditation-Body tab only, and only on a top-level row:
  // the other tabs slice the same qualifications along a different axis, so the row there
  // is a view of the data rather than the thing to edit.
  const showRowActions = !isReadonly && groupBy === "accreditationBody";
  if (showRowActions) {
    columns.push({
      id: "actions",
      header: " ",
      size: 100,
      enableSorting: false,
      enableResizing: false,
      enableColumnOrdering: false,
      enableHiding: false,
      accessorFn: (row) => row,
      Cell: ({ row }) => {
        const qual = row.original;
        return (
          <Box sx={{ display: "flex" }}>
            <EditIconButton
              size="small"
              aria-label={t({ id: "editLabel" })}
              // The row carries its chain, so the wizard's second step opens on the same
              // link selection the row was created with.
              onClick={() => setEditState(toWizardEditState(qual))}
            />
            <DeleteIconButton
              size="small"
              aria-label={t({ id: "deleteLabel" })}
              // A group deletes at Accreditation Body level, so the trash shows on group
              // rows too: handleDelete resolves the ids for a group and a single row alike.
              onClick={() => setSkuQualificationToDelete(qual)}
            />
          </Box>
        );
      },
    });
  }
  const onSkuQualificationEditClose = () => {
    createDialogOpenActions.setFalse();
    setSkuQualificationToEdit(null);
    setIsChildEdit(false);
  };

  const onSkuQualificationCreated = () => {
    queryClient.invalidateQueries(["skuQualifications", sku.id!]);
    createDialogOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuAccreditationBodyCreated" })),
    );
  };

  const onSkuQualificationUpdated = () => {
    queryClient.invalidateQueries(["skuQualifications", sku.id!]);
    createDialogOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "skuAccreditationBodyUpdated" })),
    );
  };

  // Any view can produce a group: By-LB keys on the full LB/AB/SA/Profession identity, so
  // two qualifications differing only by State or dates still collapse into one row. Drive
  // the expand affordance off the data, not off the active tab, or those rows show roll-up
  // chips with no way to open them.
  const hasExpandableRows = skuQualifications.some(
    (row) => row.isGroup && row.childQualifications.length > 1,
  );

  return (
    <Container>
      {!isReadonly && (
        <Box mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box textAlign="right">
                <Button
                  variant="outlined"
                  type="submit"
                  color="primary"
                  onClick={
                    isGroupedViewEnabled
                      ? wizardOpenActions.setTrue
                      : createDialogOpenActions.setTrue
                  }
                >
                  <T id="skuAccreditationBodyCreateButtonLabel" />
                </Button>
              </Box>
            </Grid>
          </Grid>
          <DeleteEntityDialog
            dialogTitleKey="skuAccreditationBodyDeleteTitle"
            // A group takes its member entries with it, so the confirm names how many rather
            // than asking about "this Qualification" as if it were one.
            contentKey={
              deleteMemberCount ? undefined : "skuAccreditationBodyDeleteQuestion"
            }
            rawContent={
              deleteMemberCount
                ? t(
                    { id: "skuAccreditationBodyDeleteGroupQuestion" },
                    { count: deleteMemberCount },
                  )
                : undefined
            }
            open={!!skuQualificationToDelete}
            handleDelete={handleDelete}
            onCancel={() => setSkuQualificationToDelete(null)}
          />
          <Dialog
            open={createDialogOpen}
            disableEnforceFocus
            maxWidth="md"
            fullWidth
          >
            <SkuQualificationForm
              sku={sku}
              onClose={onSkuQualificationEditClose}
              onCreate={onSkuQualificationCreated}
              onUpdate={onSkuQualificationUpdated}
              skuQualification={skuQualificationToEdit}
              isChildEdit={isChildEdit}
              {...props}
            />
          </Dialog>
          <QualificationWizardDialog
            open={wizardOpen}
            onClose={closeWizard}
            referenceRows={referenceRows}
            loadingRows={isLoadingRows}
            skuCredits={sku.credits}
            onSave={submitQualifications}
          />
          {editState && (
            <EditQualificationDialog
              open
              onClose={closeWizard}
              entries={editState.entries}
              editTarget={editState.editTarget}
              initialValues={editState.initialValues}
              disabledFields={
                (editState.editTarget?.childSkuQualificationIds?.length ?? 0) > 0
                  ? GROUP_EDIT_DISABLED_FIELDS
                  : EDIT_DISABLED_FIELDS
              }
              skuCredits={sku.credits}
              onSave={submitQualifications}
            />
          )}
          <BulkSkuQualificationConfirmDialog
            open={bulkConfirm.open}
            loading={bulkConfirm.loading}
            qualificationId={bulkConfirm.qualificationId}
            useQualificationApprovals={bulkConfirm.usesApprovals}
            previewResponse={bulkConfirm.preview}
            onConfirm={bulkConfirm.onConfirm}
            onCancel={bulkConfirm.onCancel}
            error={bulkConfirm.error}
          />
          {internalValidation.data && (
            <InternalValidationDialog
              open={internalValidation.open}
              productName={sku.title ?? ""}
              courseNumberBeingCreated={internalValidation.courseNumber}
              validationData={internalValidation.data}
              loading={internalValidation.loading}
              error={internalValidation.error}
              onProceed={internalValidation.onProceed}
              onCancel={internalValidation.onCancel}
            />
          )}
          <AcpeValidationDialog
            open={acpeValidation.open}
            skuQualificationRequest={acpeValidation.request}
            onClose={acpeValidation.onClose}
            onSave={acpeValidation.onSave}
          />
          <SnackbarMessage
            snackbarMessage={snackbarMessage}
            setSnackbarMessage={setSnackbarMessage}
          />
        </Box>
      )}

      <SkuQualificationFilter skuId={sku.id!} />

      {!isGroupedViewEnabled ? (
        <LegacyQualificationsTable
          qualifications={data?.data ?? []}
          isLoading={isFetching}
          isReadonly={isReadonly}
          onEdit={(qualification) => {
            setSkuQualificationToEdit(qualification);
            setIsChildEdit(false);
            createDialogOpenActions.setTrue();
          }}
          onChildEdit={(qualification) => {
            setSkuQualificationToEdit(qualification);
            setIsChildEdit(true);
            createDialogOpenActions.setTrue();
          }}
          onDelete={setSkuQualificationToDelete}
          onOrderChange={setOrder}
          onOrderByChange={setOrderBy}
        />
      ) : (
        <>
          <Tabs
            value={groupBy}
            onChange={(_, value: SkuQualificationGroupBy) => setGroupBy(value)}
            sx={{ mb: 1 }}
          >
            {GROUP_TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={t({ id: tab.labelId })}
              />
            ))}
          </Tabs>

          <ConfigurableTable
            isLoading={isFetching}
            settingsId="skuQualificationsTableV2"
            columns={columns}
            data={skuQualifications}
            expandedResetKey={skuQualifications}
            // The expand column is driven by renderDetailPanel presence, so a view with
            // nothing to expand passes it as undefined to keep the column out.
            enableExpanding={hasExpandableRows}
            enableExpandAll={false}
            enableClientDataOps={true}
            forcedColumnVisibility={{
              status: !creditSubmissionExpirationDateFrom,
            }}
            defaultColumnVisibility={DEFAULT_HIDDEN_COLUMNS}
            muiTableBodyRowProps={({ row }) =>
              row.original.isExpired
                ? { sx: { backgroundColor: "action.hover", opacity: 0.6 } }
                : {}
            }
            initialState={
              showRowActions
                ? { columnPinning: { right: ["actions"] } }
                : undefined
            }
            getRowCanExpand={(row) => {
              const original = row.original;
              return !!(
                original?.isGroup === true &&
                original?.childQualifications &&
                original.childQualifications.length > 1
              );
            }}
            renderDetailPanel={
              !hasExpandableRows
                ? undefined
                : ({ row, table }) => {
                    const original = row.original;
                    if (
                      !original?.isGroup ||
                      !original?.childQualifications ||
                      original.childQualifications.length <= 1
                    ) {
                      return null;
                    }
                    return (
                      <ExpandedChildTable
                        row={row}
                        table={table}
                        groupBy={groupBy}
                      />
                    );
                  }
            }
          />
        </>
      )}
    </Container>
  );
};

export default withErrorBoundary(SkuQualifications);
